import { menuItems, settingsItems } from "./constants"
import { stripEnglishPrefix } from "./authLanguageRoutes"

/**
 * Map API `module` strings (e.g. "dashboard", "all_users") to sidebar route keys
 * aligned with `menuItems[].path` in constants.js.
 */
export function normalizeModuleKey(module) {
  return String(module || "")
    .toLowerCase()
    .trim()
    .replace(/_/g, "-")
    .replace(/\s+/g, "-")
}

/** Account `role` from GET /account/profile/ (e.g. OWNER, VIEWER). */
export function normalizeProfileAccountRole(role) {
  return String(role ?? "").trim().toUpperCase()
}

/** Module permission rows from GET /account/profile/ (multiple API shapes). */
export function getModulePermissionsListFromProfile(user) {
  if (!user || typeof user !== "object") return []

  const mrp = user.market_role_permissions
  if (Array.isArray(mrp)) return mrp
  if (mrp && typeof mrp === "object" && Array.isArray(mrp.module_permissions)) {
    return mrp.module_permissions
  }

  if (Array.isArray(user.admin_role_module_permissions)) {
    return user.admin_role_module_permissions
  }
  if (Array.isArray(user.module_permissions)) {
    return user.module_permissions
  }

  return []
}

export function userHasScopedModulePermissions(user) {
  return getModulePermissionsListFromProfile(user).length > 0
}

/** Market portal user from GET /account/profile/ (admin flag, module list, or legacy role). */
export function isProfileMarketPortalUser(user) {
  if (!user || typeof user !== "object") return false
  if (user.is_market_admin === true) return true
  if (userHasScopedModulePermissions(user)) return true
  const r = normalizeProfileAccountRole(user.role)
  return (r === "OWNER" || r === "VIEWER") && user.market_role_permissions != null
}

/**
 * Profile market portal wins for super-admin UI. Otherwise JWT `highest_role`.
 * @param {object|null|undefined} user — Redux profile from `/account/profile/`
 * @param {string|null|undefined} jwtHighestRole — `decoded.highest_role` from token
 */
export function effectiveHighestRole(user, jwtHighestRole) {
  if (isProfileMarketPortalUser(user)) return "SUPERADMIN"
  const jwt = jwtHighestRole != null ? String(jwtHighestRole).trim() : ""
  if (jwt) return jwtHighestRole
  return jwtHighestRole ?? null
}

/**
 * Backend module catalog: `module_id` / `key` from GET /account/profile/
 * `market_role_permissions.module_permissions` → `menuItems[].path` in constants.js.
 * (API labels: "AI Users" → path `all-users`, "Merchant Users" → path `users`.)
 */
const MODULE_ID_TO_MENU_PATH = new Map([
  [1, "dashboard"],
  [2, "merchants"],
  [3, "stores"],
  [4, "users"],
  [5, "all-users"],
  [6, "campaigns"],
  [7, "discounts"],
  [8, "referrals"],
  [9, "announcements"],
  [10, "library"],
  [11, "categories"],
  [12, "contacts"],
  [13, "plans-pricing"],
  [14, "plans-pricing/usage"],
  [15, "sales-requests"],
  [16, "site-policies"],
])

/** Normalized API `key` / `module` string → sidebar route key (`menuItems.path`). */
const API_KEY_TO_MENU_PATH = new Map([
  ["dashboard", "dashboard"],
  ["merchants", "merchants"],
  ["stores", "stores"],
  ["merchant-users", "users"],
  ["merchant_users", "users"],
  ["users", "users"],
  ["ai-users", "all-users"],
  ["ai_users", "all-users"],
  ["all-users", "all-users"],
  ["all_users", "all-users"],
  ["campaigns", "campaigns"],
  ["discounts", "discounts"],
  ["referrals", "referrals"],
  ["announcements", "announcements"],
  ["library", "library"],
  ["categories", "categories"],
  ["contacts", "contacts"],
  ["create-roles", "create-roles"],
  ["admin-roles", "create-roles"],
  // Backend key `plan_usage` normalizes to "plan-usage"; the sidebar route is a
  // sub-path of plans-pricing.
  ["plan-usage", "plans-pricing/usage"],
  ["plans", "plans-pricing"],
  ["sales-requests", "sales-requests"],
  ["site-policies", "site-policies"],
])

function apiKeyToMenuPathKey(normalizedKey) {
  if (!normalizedKey) return ""
  return API_KEY_TO_MENU_PATH.get(normalizedKey) ?? normalizedKey
}

/** Normalize API `module` / `key` string → `menuItems.path` (for role tables + profile). */
export function apiModuleStringToMenuPathKey(str) {
  return apiKeyToMenuPathKey(normalizeModuleKey(str))
}

/**
 * Fallback for Create Roles UI when GET superadmin-role-modules is empty:
 * same `module_id` → path as profile/sidebar.
 */
export function getSuperadminRoleModuleCatalogFallback() {
  return Array.from(MODULE_ID_TO_MENU_PATH.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([module_id, path]) => ({ path, module_id }))
}

/**
 * One row from `market_role_permissions.module_permissions` → sidebar key (`item.path` style).
 * Supports `{ module }` string or `{ module_id }` from GET /account/profile/.
 */
export function modulePermissionRowToKey(row) {
  if (!row || typeof row !== "object") return ""
  // The id wins when it is one we know: `/account/profile/` sends the module's
  // display label rather than its key, and every label happens to normalize to
  // its key except "Policies & Privacy", which is the `site_policies` module.
  const mid = Number(row.module_id ?? row.moduleId)
  if (Number.isFinite(mid) && MODULE_ID_TO_MENU_PATH.has(mid)) {
    return MODULE_ID_TO_MENU_PATH.get(mid)
  }

  const rawLabel =
    row.module ??
    row.key ??
    row.module_name ??
    row.moduleName ??
    ""
  const fromModule =
    rawLabel != null && String(rawLabel).trim() !== ""
      ? normalizeModuleKey(String(rawLabel).trim())
      : ""
  if (fromModule) return apiKeyToMenuPathKey(fromModule)
  return ""
}

/**
 * @returns {Set<string>|null} null = do not filter by module permissions (use JWT market_role / legacy rules)
 */
export function getAllowedModuleKeysFromProfile(user) {
  const list = getModulePermissionsListFromProfile(user)
  if (list.length === 0) return null

  const keys = new Set()
  for (const m of list) {
    const k = modulePermissionRowToKey(m)
    if (k) keys.add(k)
  }

  return keys
}

/**
 * Path of the single sidebar entry that should render as active, or "" if none.
 *
 * Most specific match wins: a nested route that has its own entry
 * ("plans-pricing/usage") must not also light up its parent ("plans-pricing").
 * Only when nothing matches the full route do we fall back to the first
 * segment, so nested pages *without* an entry of their own
 * (/plans-pricing/create, /stores/create-store) still highlight their parent.
 *
 * @param {string[]} routeSegments — locale prefix already stripped
 * @param {{path: string}[]} items — menuItems + organizationMenuItems
 */
export function resolveActiveSidebarPath(routeSegments, items) {
  const norm = (p) => String(p || "").replace(/^\//, "")
  const currentRoute = routeSegments.join("/")
  if (!currentRoute) return ""

  const exact = (items || []).find((i) => norm(i.path) === currentRoute)
  if (exact) return norm(exact.path)

  const firstSegment = routeSegments[0] || ""
  // "organization" owns only its own page; its children carry their own entries,
  // so an unknown /organization/* route highlights nothing rather than the team
  // page.
  if (firstSegment === "organization" && routeSegments.length > 1) return ""

  // Walk the prefixes longest-first so a page nested under an entry that is
  // itself nested (/plans-pricing/usage/tools) highlights that entry rather than
  // falling straight through to its parent (/plans-pricing). The last iteration
  // is the first segment, which is the original parent lookup.
  for (let depth = routeSegments.length - 1; depth > 0; depth -= 1) {
    const prefix = routeSegments.slice(0, depth).join("/")
    const match = (items || []).find((i) => norm(i.path) === prefix)
    if (match) return norm(match.path)
  }
  return ""
}

/** First URL segment → sidebar module key (e.g. `/dashboard/foo` → `dashboard`). */
export function pathnameToModuleKey(pathname) {
  const segment = String(stripEnglishPrefix(pathname) || "")
    .replace(/^\//, "")
    .split("/")[0]
  if (!segment) return ""
  if (segment === "create-roles") return "create-roles"
  return normalizeModuleKey(segment)
}

export function isPathAllowedByModulePermissions(pathname, allowedModules) {
  if (!allowedModules) return true
  // Strip `/en` so English routes are checked against real module keys (not `"en"`).
  const path = String(stripEnglishPrefix(pathname) || "")
  if (path === "/" || path === "") return true

  if (path.startsWith("/organization")) {
    return true
  }

  // Account settings are always allowed for every role (not a permission module).
  if (path.startsWith("/setting")) {
    return true
  }

  if (path.startsWith("/dashboard")) {
    return allowedModules.has("dashboard")
  }
  if (path.startsWith("/create-roles")) {
    return allowedModules.has("create-roles")
  }
  // Its own permission module, so it must be checked before the first-segment
  // rule below — that would resolve it to "plans-pricing" and let anyone who
  // can edit plans read the usage report.
  if (path.startsWith("/plans-pricing/usage")) {
    return allowedModules.has("plans-pricing/usage")
  }

  const key = pathnameToModuleKey(path)
  if (!key) return true
  return allowedModules.has(key)
}

/**
 * Home path after login / catch-all when `module_permissions` scopes the sidebar.
 * If not scoped, or `dashboard` is allowed, returns `/dashboard`.
 * Otherwise first allowed item in main menu order, then settings.
 */
function profileIsOrgOwner(user) {
  if (!user || typeof user !== "object") return false
  const profile =
    user.user && typeof user.user === "object" ? user.user : user
  return profile.is_org_owner === true
}

export function getPostLoginPathFromProfile(user) {
  if (profileIsOrgOwner(user)) return "/organization"
  const allowed = getAllowedModuleKeysFromProfile(user)
  if (!allowed) return "/dashboard"
  if (allowed.has("dashboard")) return "/dashboard"
  for (const item of menuItems) {
    const key = normalizeModuleKey(item.path)
    if (item.name === "Admin roles") {
      if (allowed.has("create-roles")) return "/create-roles"
      continue
    }
    if (allowed.has(key)) return `/${item.path}`
  }
  for (const s of settingsItems) {
    const raw = String(s.path || "").replace(/^\//, "")
    const key = normalizeModuleKey(raw)
    if (allowed.has(key)) {
      const p = String(s.path || "")
      return p.startsWith("/") ? p : `/${raw}`
    }
  }
  return "/dashboard"
}

export function menuItemMatchesAllowedModules(item, allowedModules) {
  if (!allowedModules) return true
  if (item.name === "Admin roles") {
    return allowedModules.has("create-roles")
  }
  const key = normalizeModuleKey(item.path)
  return allowedModules.has(key)
}

/** Single row from `market_role_permissions.module_permissions` for this sidebar/API module key */
export function getModulePermissionEntry(user, moduleKey) {
  const list = getModulePermissionsListFromProfile(user)
  if (list.length === 0) return null
  const key = normalizeModuleKey(moduleKey)
  return list.find((m) => modulePermissionRowToKey(m) === key) || null
}

function normalizeAccessType(accessType) {
  return String(accessType ?? "")
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/-/g, "_")
}

/** API `edit_only`: create and change records, but never delete them. */
export function isAccessTypeNoDelete(accessType) {
  const t = normalizeAccessType(accessType)
  return t === "edit_only" || t === "no_delete" || t === "edit"
}

/**
 * True when API marks module as view-only. Unknown/missing `access_type` defaults to read-only
 * when an entry exists (safe default). Write access: full, full_access, write, edit, edit_only, manage.
 */
export function isAccessTypeReadOnly(accessType) {
  const t = normalizeAccessType(accessType)
  if (!t) return true
  if (
    t === "full" ||
    t === "full_access" ||
    t === "write" ||
    t === "manage" ||
    isAccessTypeNoDelete(t)
  ) {
    return false
  }
  return true
}

/** True when profile lists this module and its access is not full write */
export function isProfileModuleReadOnly(user, moduleKey) {
  const row = getModulePermissionEntry(user, moduleKey)
  if (!row) return false
  return isAccessTypeReadOnly(row.access_type ?? row.access)
}

/**
 * True when the profile lists this module and the caller may not delete in it:
 * read-only roles cannot delete, and `edit_only` roles can do everything else.
 */
export function isProfileModuleDeleteBlocked(user, moduleKey) {
  const row = getModulePermissionEntry(user, moduleKey)
  if (!row) return false
  const access = row.access_type ?? row.access
  return isAccessTypeReadOnly(access) || isAccessTypeNoDelete(access)
}

/**
 * A verified super administrator: the account flag from the profile or the
 * signed token. Unlike `effectiveHighestRole`, this is never true for a
 * delegated market-portal role.
 */
export function isAccountSuperuser(user, jwtDecoded) {
  return (
    user?.is_superuser === true ||
    user?.is_super_user === true ||
    user?.market_role_permissions?.is_superuser === true ||
    jwtDecoded?.is_superuser === true ||
    jwtDecoded?.is_super_user === true
  )
}

/** Delete allowed in this module: superusers always; otherwise not read-only and not edit_only. */
export function canDeleteInModule(user, moduleKey, jwtDecoded) {
  if (isAccountSuperuser(user, jwtDecoded)) return true
  return !isProfileModuleDeleteBlocked(user, moduleKey)
}

/** Plans & Pricing: list-only when module is read_only; superusers always have full access. */
export function isPlansPricingReadOnly(user, jwtDecoded) {
  const marketRole =
    jwtDecoded?.market_role != null ? String(jwtDecoded.market_role) : ""
  if (marketRole === "2" || marketRole === "3") return true
  if (isAccountSuperuser(user, jwtDecoded)) return false
  return isProfileModuleReadOnly(user, "plans-pricing")
}

/** Policies & Privacy: view-only when module is read_only; superusers always have full access. */
export function isSitePoliciesReadOnly(user, jwtDecoded) {
  if (isAccountSuperuser(user, jwtDecoded)) return false
  return isProfileModuleReadOnly(user, "site-policies")
}

/** AI Users: create dashboard user when `ai_users` is full_access or account is superuser */
export function canCreateDashboardUser(user, jwtDecoded) {
  if (isAccountSuperuser(user, jwtDecoded)) {
    return true
  }
  const row = getModulePermissionEntry(user, "all-users")
  if (!row) return false
  return !isAccessTypeReadOnly(row.access_type ?? row.access)
}
